import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  untracked,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { Observable, of } from 'rxjs';
import { AccessRevokedNoticeService } from '../../access-revoked-notice.service';
import * as fromFeatureFlags from '../../feature-flags';
import * as fromLayout from '../../layout';
import { bindPageTitle } from '../../page-title';
import { KnowledgeModalComponent } from '../assistants/knowledge-modal.component';
import { SkillBuilderHostComponent } from '../assistants/skill-builder-host.component';
import {
  assistantsFeature,
  AssistantsState,
} from '../assistants/store/assistants.reducer';
import { ChatDisclaimerComponent } from '../components/chat-disclaimer.component';
import { ImageOverlayViewerSmartComponent } from '../components/image-overlay-viewer/image-overlay-viewer.smart.component';
import { IntroductionComponent } from '../components/introduction.component';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import { ConversationPromptInputSmartComponent } from '../conversation/prompt-input/conversation-prompt-input.smart.component';
import * as fromShared from '../shared';
import {
  ChatActions,
  chatsFeature,
  ChatsState,
  ChatWithMessagesActions,
} from '../store';
import { ChatCreationService } from '../store/chat-creation.service';
import { teammatesFeature, TeammatesState } from './store';
import { TeammateAssistantComponent } from './teammate-assistant.component';
import { getTeammateVisualFromParts } from './teammate-avatar.config';

@Component({
  imports: [
    TopBarMobileResponsive,
    TranslateModule,
    TeammateAssistantComponent,
    IntroductionComponent,
    SkillBuilderHostComponent,
    KnowledgeModalComponent,
    ChatDisclaimerComponent,
    ConversationPromptInputSmartComponent,
    ImageOverlayViewerSmartComponent,
    fromSeriousSystem.ToastComponent,
  ],
  // The composer injects these two, which only the conversation page provided
  // before (SB-3978). Everything else it needs comes from the /chats routes.
  providers: [fromShared.FileUploadV2Store, fromShared.ChatImagePreviewStore],
  template: `
    <div class="bg-gradient-e-vertical h-svh w-full flex flex-col relative">
      @if (isMobile()) {
        <squadbox-top-bar-mobile
          class="bg-shades-white"
          (chevronLeftButtonClicked)="handleChevronLeftButtonCTA()"
        >
        </squadbox-top-bar-mobile>
      }

      @let teammate = selectedTeammate();
      <!--
        We want the scrollbar to be flush with the right side of the screen, not flush with the right side of the content.
        If the size of the viewport is > 1320px, this one-element div ensures, that the scrollbar is positioned correctly
      -->
      <div
        class="w-full flex-1 min-h-0 flex flex-col overflow-y-auto fade-in-subtle"
      >
        @if (teammate) {
          <div
            class="
            flex flex-col gap-6
            w-full max-w-370 self-center
            tablet-portrait:px-20 px-6
            tablet-portrait:pt-22 pt-6
            pb-44
          "
          >
            <squadbox-introduction
              [teammate]="true"
              [visual]="selectedTeammateVisual()!"
              [title]="teammate.welcomeMessage"
              [subtitle]="teammate.purposeMessage"
            />

            <!-- Teammate Assistants -->
            <div
              class="
              grid gap-2 tablet-portrait:gap-4
              grid-cols-3 max-[1194px]:grid-cols-2 max-[650px]:grid-cols-1
            "
            >
              @for (assistant of teammate.assistants; track assistant.uuid) {
                <squadbox-teammate-assistant-card
                  class="group"
                  [cardData]="assistant"
                  [isMobile]="isMobile()"
                />
              }
            </div>
          </div>
        }
      </div>

      <!-- The conversation box, outside the scroll container so it stays pinned
           under the cards. The composer is the Figma "Prompt Input Area"
           instance: the existing chat box, bound to this Teammate (SB-3978). -->
      @if (teammate) {
        <div
          class="
            absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 px-4
            tablet-portrait:px-10 pb-4
            bg-linear-to-b from-neutral-100/0 to-neutral-100 to-64%
          "
        >
          <!-- A refused first message (SB-3978 B2-T38, plan Decision 4): the
               chat is back as it was, and the refusal shows here, as on the
               conversation page (SB-3944 / KD-41). -->
          @if (firstSendRefusal(); as refusal) {
            @if (!refusal.errorDismissed) {
              <sd-toast
                class="fade-in relative z-10 self-center max-w-fit"
                [message]="refusal.error"
                [type]="'sd-toast-error'"
                (dismissClicked)="handleFirstSendRefusalDismissClicked()"
              />
            }
          }
          @if (promptInputShown()) {
            <div
              class="w-full tablet-portrait:max-w-180"
              [style.view-transition-name]="'prompt-input'"
            >
              <squadbox-conversation-prompt-input-smart
                host="teammate"
                [teammateId]="teammate.id"
                [isThinking]="selectedChatIsThinking()"
              />
            </div>
          }
          <squadbox-chat-disclaimer [short]="isMobile()" />
        </div>
      }

      <!-- Both pencil destinations (SB-3650 / T5, KD-32). Neither overlay was
           mounted on this route, so both branches of the pencil were dead
           clicks here. The host owns the builder's @if and both dismissal
           prompts; the knowledge modal is created per opening, which is what
           resets its transaction id and staged list. -->
      <squadbox-skill-builder-host />
      @if (knowledgeModal().kind !== 'closed') {
        <squadbox-knowledge-modal />
      }

      <!-- The composer's staged-image chips open this (SB-3978 B2-T32). MUST
           stay inside this page's injector: it reads a staged image's preview
           from this page's ChatImagePreviewStore, which the composer fills from
           this page's FileUploadV2Store, and falls back to a presigned URL. -->
      <squadbox-image-overlay-viewer-smart />
    </div>
  `,
})
export class TeammatePage {
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly teammatesStore = inject<Store<TeammatesState>>(Store);
  private readonly assistantsStore = inject<Store<AssistantsState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);

  /** `ff-teammate-prompt-input` (SB-3978): off hides this page's composer. */
  protected readonly promptInputShown = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectTeammatePromptInput,
  );
  private readonly accessRevokedNotice = inject(AccessRevokedNoticeService);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );

  public readonly slug = input.required<string>();

  private readonly areTeammatesLoaded = this.teammatesStore.selectSignal(
    teammatesFeature.selectLoaded,
  );
  private readonly teammatesReloading = this.teammatesStore.selectSignal(
    teammatesFeature.selectIsLoading,
  );
  private readonly rawTeammates = this.teammatesStore.selectSignal(
    teammatesFeature.selectAll,
  );
  private readonly accessReloadUnsettled = this.chatsStore.selectSignal(
    fromShared.selectAccessReloadUnsettled,
  );

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  /** Edit knowledge modal state (SB-3650 / T2 ships the slice, T3 the modal). */
  public readonly knowledgeModal = this.assistantsStore.selectSignal(
    assistantsFeature.selectKnowledgeModal,
  );
  private readonly builderHost = viewChild(SkillBuilderHostComponent);
  private readonly selectedChat = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChat,
  );
  private readonly chatEntities = this.chatsStore.selectSignal(
    chatsFeature.selectEntities,
  );
  private readonly chatCreation = inject(ChatCreationService);
  /**
   * The chat this page instance shows (SB-3978 B2-T38). Kept once the first
   * send gives it a message, so the page does not start another chat while
   * that send is in flight; a new visit starts from the empty one again.
   */
  private pageChatUuid: string | null = null;
  /** A refused first send on this page's chat (plan Decision 4). */
  public readonly firstSendRefusal = computed(
    () => this.selectedChat()?.firstSendRefusal ?? null,
  );
  /** The value the conversation page passes the box: the selected chat's. */
  public readonly selectedChatIsThinking = computed(
    () => this.selectedChat()?.isThinking ?? false,
  );
  public readonly selectedTeammate = computed(() => {
    const slug = this.slug();

    return slug
      ? this.teammatesStore.selectSignal(
          teammatesFeature.selectTeammateBySlug(slug),
        )()
      : undefined;
  });

  /**
   * The selected teammate's resolved visual, precomputed.
   *
   * This page is CheckAlways, so calling the resolver straight from the
   * template rebuilt the object on every change-detection pass, rewriting the
   * avatar's input signal each tick and re-dirtying its initialization effect
   * (which resets any in-flight hover animation). SB-3519 widened this from
   * the avatar config to the visual union, which is built with spreads and so
   * is just as reference-unstable — the memoization matters as much as before,
   * not less.
   */
  public readonly selectedTeammateVisual = computed(() => {
    const teammate = this.selectedTeammate();

    return teammate ? getTeammateVisualFromParts(teammate) : undefined;
  });

  constructor() {
    // B2-T32: the first send keeps this chat selected, so no `selectChat` runs
    // to clear an overlay left open; without this it would reappear on the
    // conversation page.
    inject(DestroyRef).onDestroy(() => {
      this.chatsStore.dispatch(
        fromShared.ImageOverlayActions.closeImageOverlay(),
      );
    });

    // SB-3978 B2-T38 (plan Decision 3): show this Teammate's chat. The tab
    // keeps one empty chat per Teammate and reuses it, so typed text and files
    // stay (SB-3944 / KD-56); with none, the page starts a Teammate draft,
    // which the first send creates with `POST /chats`. Nothing to release on
    // close: a general "New chat" is a draft of its own.
    effect(() => {
      const teammate = this.selectedTeammate();
      const entities = this.chatEntities();
      const selected = this.selectedChat();
      if (!teammate) return;
      untracked(() => {
        // A 409 on the first create moves the draft to a new uuid (KD-18).
        const current = this.pageChatUuid
          ? entities[this.chatCreation.latestUuid(this.pageChatUuid)]
          : undefined;
        const chat =
          current?.teammateId === teammate.id
            ? current
            : fromShared.emptyTeammateChatOf(entities, teammate.id);
        if (!chat) {
          // The entities change, so this effect runs again and selects it.
          this.pageChatUuid = this.routerService.startTeammateDraft(
            teammate.id,
          );
          return;
        }
        this.pageChatUuid = chat.uuid;
        if (selected?.uuid !== chat.uuid) {
          this.chatsStore.dispatch(ChatActions.selectChat({ uuid: chat.uuid }));
        }
      });
    });

    bindPageTitle(() => this.selectedTeammate()?.name);

    // Remember the slug we've actually resolved a teammate for, so a live
    // revocation (it was visible, now it's gone) is distinguishable from a
    // never-accessible slug (a typo or stale link that was never theirs).
    let resolvedSlug: string | null = null;

    effect(() => {
      const slug = this.slug();
      if (!slug) return;

      const loaded = this.areTeammatesLoaded();
      if (!loaded) return;

      const teammate = this.selectedTeammate();
      if (teammate) {
        resolvedSlug = slug;
        return;
      }

      // B2-T32: a nudge reloads Teammates and skills as two requests and the
      // Teammate view joins both, so a Teammate reached through a swapped skill
      // looks absent until both land, or, after a failed skills reload, until
      // one succeeds. Wait for that, for the toast and for the 404 alike. A
      // Teammate gone from the raw list is a real loss: not held.
      const goneFromList =
        !this.teammatesReloading() &&
        !this.rawTeammates().some((candidate) => candidate.slug === slug);
      if (this.accessReloadUnsettled() && !goneFromList) {
        return;
      }

      // Absent + loaded: if we had resolved this exact slug, the teammate was
      // revoked out from under the user → landing + "access removed" toast.
      // Otherwise it was never theirs → a plain 404, as before.
      if (resolvedSlug === slug) {
        void this.accessRevokedNotice.notify('TOAST.ACCESS_REVOKED.TEAMMATE');
      } else {
        void this.routerService.navigateToNotFound();
      }
    });
  }

  public handleFirstSendRefusalDismissClicked(): void {
    const chatUuid = this.selectedChat()?.uuid;
    if (chatUuid) {
      this.chatsStore.dispatch(
        ChatWithMessagesActions.dismissFirstSendRefusal({ chatUuid }),
      );
    }
  }

  public async handleChevronLeftButtonCTA(): Promise<void> {
    await this.mobileNavigation.open();
  }

  /**
   * CanDeactivate hook, delegated to the builder host exactly as the assistants
   * page does (SB-3650 / T5, KD-32). Registered on this route in
   * `app.routes.ts`;
   *
   * `true` when the host is not present. Unlike the pre-T5 code this replaced,
   * that branch is not a live case: the host is rendered UNCONDITIONALLY, so it
   * resolves whenever this page has rendered at all, whereas the old
   * `viewChild(AssistantBuilderPage)` sat under an `@if` and was legitimately
   * undefined while the slice was open. If it is ever made conditional, this
   * branch has to close both slices the way the host does.
   *
   * It also runs on a teammate-to-teammate slug change, because this route sets
   * `runGuardsAndResolvers: 'paramsOrQueryParamsChange'` and the component is
   * reused. With a clean open builder the allow path closes it, which is what
   * we want — the slice lives on the parent route and would otherwise re-open
   * over the newly selected teammate.
   */
  public canDeactivate(): Observable<boolean> {
    return this.builderHost()?.canDeactivate() ?? of(true);
  }
}
